import type { ImportSummary } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';

interface ImportSummaryViewProps {
  summary: ImportSummary;
  // Optional: caps the number of errors displayed inline (rest shown via toggle).
  errorPreviewLimit?: number;
}

export function ImportSummaryView({ summary, errorPreviewLimit = 25 }: ImportSummaryViewProps) {
  const errs = summary.errors.slice(0, errorPreviewLimit);
  const hiddenCount = summary.errors.length - errs.length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total rows" value={summary.totalRows} />
        <StatCard label="Will commit" value={summary.validRows} tone="success" />
        <StatCard
          label="Errors"
          value={summary.invalidRows}
          tone={summary.invalidRows > 0 ? 'danger' : 'neutral'}
        />
        <StatCard
          label="Warnings"
          value={summary.warnings.length}
          tone={summary.warnings.length > 0 ? 'warn' : 'neutral'}
        />
      </div>

      {summary.warnings.length > 0 && (
        <details className="surface p-4">
          <summary className="cursor-pointer font-medium">
            {summary.warnings.length} warning{summary.warnings.length === 1 ? '' : 's'} (won't block
            import)
          </summary>
          <ul className="mt-3 space-y-2 text-[var(--text-sm)]">
            {summary.warnings.map((w, i) => (
              <li key={i} className="flex gap-2">
                <Badge tone="warn">{w.kind}</Badge>
                <span className="text-[var(--color-ink-muted)]">{w.message}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {summary.errors.length > 0 && (
        <div role="region" aria-label="Import errors" className="surface overflow-hidden">
          <header className="border-b border-[var(--color-border)] bg-[var(--color-danger-muted)] px-4 py-3">
            <h3 className="font-medium">Errors</h3>
            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              Fix the rows below in your spreadsheet and re-upload, or commit only the valid rows.
            </p>
          </header>
          <table className="w-full text-left text-[var(--text-sm)]">
            <thead className="bg-[var(--color-surface-sunken)]">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  Row
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Column
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Issue
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Detail
                </th>
              </tr>
            </thead>
            <tbody>
              {errs.map((e, i) => (
                <tr key={i} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-2 tabular-nums">{e.row === 0 ? '—' : e.row}</td>
                  <td className="px-4 py-2 font-mono text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                    {e.column ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone="danger">{e.kind}</Badge>
                  </td>
                  <td className="px-4 py-2 text-[var(--color-ink-muted)]">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {hiddenCount > 0 && (
            <p className="px-4 py-3 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              …and {hiddenCount} more.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'success' | 'warn' | 'danger';
}) {
  const colorFor: Record<typeof tone, string> = {
    neutral: 'var(--color-ink)',
    success: 'var(--color-success)',
    warn: 'var(--color-warn)',
    danger: 'var(--color-danger)',
  };
  return (
    <div className="surface-elevated p-4">
      <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
        {label}
      </p>
      <p
        className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums"
        style={{ color: colorFor[tone] }}
      >
        {value}
      </p>
    </div>
  );
}
